import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import { FiArrowLeft } from 'react-icons/fi';
import axiosInstance from '../../services/AxiosInstance';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { clearSubscriptionCache } from '../../utils/subscriptionCache';
import { clearSubscriptionCache as clearFeatureCache } from '../../utils/featureLock';
import { getUserDefaultLandingPage, getLandingPathForSubscriptionModuleId } from '../../utils/userSettings';
import {
  getCheckoutIdFromSearchParams,
  clearPaymenntCheckoutSession,
  PAYMENNT_CHECKOUT_ID_KEY
} from '../../utils/paymentCheckoutParams';
import { formatFlowdoModuleOnly } from '../../utils/subscriptionPlanLabels';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './SubscriptionPaymentResult.css';
import './SubscriptionPaymentSuccess.css';

/**
 * Dedicated page shown after a successful subscription payment.
 * Confirms status via /payment/status; on failure redirects to checkout (same as before).
 */
const SubscriptionPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | success
  const [message, setMessage] = useState('');
  const [checkoutId, setCheckoutId] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  useEffect(() => {
    const checkoutIdParam =
      getCheckoutIdFromSearchParams(searchParams) ||
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(PAYMENNT_CHECKOUT_ID_KEY) : null);

    if (!checkoutIdParam) {
      navigate('/user-subscription', { replace: true });
      return;
    }

    setCheckoutId(checkoutIdParam);
    clearPaymenntCheckoutSession();
    checkPaymentStatus(checkoutIdParam);
  }, [searchParams, navigate]);

  const refreshSubscriptionData = () => {
    clearSubscriptionCache();
    clearFeatureCache();
    try {
      window.dispatchEvent(new Event('user-subscription-updated'));
    } catch (e) {}
  };

  const checkPaymentStatus = async (id) => {
    try {
      const response = await axiosInstance.get(`/payment/status/${id}`);
      const data = response.data;
      const s = (data.status || '').toLowerCase();

      if (s === 'completed' || s === 'paid') {
        setStatus('success');
        try {
          localStorage.removeItem('subscription_checkout_plan');
        } catch (e) {}
        setMessage(
          'Your payment has been successfully processed and your subscription is now active. We hope you enjoy using flowdo products.'
        );
        setSubscriptionDetails(data.subscriptionDetails || null);
        refreshSubscriptionData();
      } else if (s === 'failed' || s === 'cancelled' || s === 'refunded') {
        navigate(
          `/subscription/checkout?checkout=${encodeURIComponent(id)}`,
          { replace: true }
        );
      } else {
        setTimeout(() => checkPaymentStatus(id), 3000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (error.response?.status === 404) {
        navigate('/user-subscription', { replace: true });
        return;
      }
      setTimeout(() => checkPaymentStatus(id), 5000);
    }
  };

  const handleBackToSubscriptions = () => {
    refreshSubscriptionData();
    navigate('/user-subscription');
  };

  const handleContinue = () => {
    refreshSubscriptionData();
    if (subscriptionDetails?.is_bundle) {
      navigate(getUserDefaultLandingPage());
      return;
    }
    const modulePath = getLandingPathForSubscriptionModuleId(subscriptionDetails?.module_id);
    navigate(modulePath || getUserDefaultLandingPage());
  };

  const handleViewSubscription = () => {
    refreshSubscriptionData();
    navigate('/user-subscription');
  };

  const handleDownloadInvoice = async () => {
    if (!checkoutId) {
      toast.error('Unable to download invoice. Please try again from your subscriptions.');
      return;
    }
    try {
      const response = await axiosInstance.get(`/payment/invoice/${checkoutId}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${checkoutId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      window.setTimeout(() => {
        toast.success('Invoice downloaded successfully.');
      }, 0);
    } catch (error) {
      let msg = 'Failed to download invoice.';
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            msg = json.message || json.error || msg;
          } catch (_) {}
        } else if (typeof error.response.data === 'object') {
          msg = error.response.data.message || error.response.data.error || msg;
        }
        if (error.response.status === 404) {
          msg = msg || 'Invoice not available yet. Please try again in a moment.';
        }
      }
      toast.error(msg);
    }
  };

  return (
    <div className="subscription-payment-result-page subscription-payment-success-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <button
        type="button"
        className="payment-result-back-button"
        onClick={handleBackToSubscriptions}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <Row style={{ marginBottom: '0px !important' }}>
        <Col lg={12}>
          <Card className="user-list-card payment-result-card">
            <Card.Body className="payment-result-body">
              {status === 'checking' && (
                <>
                  <div className="d-flex flex-column align-items-start py-4 payment-success-loading">
                    <Spinner animation="border" style={{ color: '#C42126' }} />
                    <p className="mt-3 text-muted mb-0">We are processing your payment. Please do not close this window.</p>
                  </div>
                </>
              )}

              {status === 'success' && (
                <>
                  <h2 className="success-title mb-2">Thank you!</h2>
                  <p className="mb-4">{message}</p>
                  <p className="support-contact mb-4">
                    Should you require any support please feel free to write to us:{' '}
                    <a href="mailto:support@flowdo.biz">support@flowdo.biz</a>
                  </p>

                  {subscriptionDetails && (
                    <div className="payment-result-details mb-4">
                      <h6 className="payment-result-details-title mb-3">Your Subscription Details</h6>
                      <div className="checkout-detail-rows">
                        {subscriptionDetails.is_bundle &&
                        Array.isArray(subscriptionDetails.line_items) &&
                        subscriptionDetails.line_items.length > 1 ? (
                          <>
                            {subscriptionDetails.line_items.map((row, idx) => (
                              <div key={`${row.subscription_plan_id}-${idx}`} className="checkout-detail-row">
                                <span className="text-muted">
                                  {row.subscription_display_label ||
                                    row.plan_name ||
                                    `Plan ${row.subscription_plan_id}`}
                                </span>
                                <span className="fw-semibold checkout-detail-value">
                                  {Number(row.amount ?? 0).toFixed(2)} {subscriptionDetails.currency || 'USD'}
                                </span>
                              </div>
                            ))}
                            <div className="checkout-detail-row checkout-detail-row-total">
                              <span className="fw-bold">Total paid</span>
                              <span className="fw-bold checkout-detail-value">
                                {Number(subscriptionDetails.amount).toFixed(2)}{' '}
                                {subscriptionDetails.currency || 'USD'}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            {formatFlowdoModuleOnly(subscriptionDetails.module_id) && (
                              <div className="checkout-detail-row">
                                <span className="text-muted">Module</span>
                                <span className="fw-semibold checkout-detail-value">
                                  {formatFlowdoModuleOnly(subscriptionDetails.module_id)}
                                </span>
                              </div>
                            )}
                            <div className="checkout-detail-row">
                              <span className="text-muted">Plan</span>
                              <span className="fw-semibold checkout-detail-value">
                                {formatFlowdoModuleOnly(subscriptionDetails.module_id)
                                  ? subscriptionDetails.planName ||
                                    subscriptionDetails.subscriptionDisplayLabel
                                  : subscriptionDetails.subscriptionDisplayLabel ||
                                    subscriptionDetails.planName}
                              </span>
                            </div>
                            <div className="checkout-detail-row">
                              <span className="text-muted">Period</span>
                              <span className="fw-semibold checkout-detail-value">{subscriptionDetails.period}</span>
                            </div>
                            <div className="checkout-detail-row">
                              <span className="text-muted">Amount</span>
                              <span className="fw-semibold checkout-detail-value">
                                {Number(subscriptionDetails.amount).toFixed(2)}{' '}
                                {subscriptionDetails.currency || 'USD'}
                              </span>
                            </div>
                            <div className="checkout-detail-row checkout-detail-row-total">
                              <span className="fw-bold">Totals</span>
                              <span className="fw-bold checkout-detail-value">
                                {Number(subscriptionDetails.amount).toFixed(2)}{' '}
                                {subscriptionDetails.currency || 'USD'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="success-buttons d-flex flex-column flex-sm-row gap-2 flex-wrap">
                    <button
                      type="button"
                      className="payment-result-btn-outline payment-result-btn"
                      onClick={handleDownloadInvoice}
                    >
                      <i className="fas fa-download me-2"></i>
                      Download Invoice
                    </button>
                    <button type="button" className="new-user-btn payment-result-btn" onClick={handleContinue}>
                      <i className="fas fa-arrow-right me-2"></i>
                      Continue
                    </button>
                  </div>
                  <p className="invoice-email-note text-muted small mt-4 mb-0">
                    We have also emailed the invoice to the registered email address.
                  </p>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <InternalFooter />
    </div>
  );
};

export default SubscriptionPaymentSuccess;
